"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import { useQuery } from "convex/react";
import { BarChartIcon, CreditCardIcon, FolderOpenDot, ListIcon, LifeBuoyIcon, SettingsIcon, ShieldCheckIcon } from "lucide-react";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import * as React from "react";
import { useOpenSupportDialog } from "@/components/support/support-dialog";
import { Sidebar, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { useIsAdmin } from "@/lib/auth/role-utils";
import { SharedSidebar, type SharedNavGroup } from "./shared-sidebar";

// Support opens a dialog instead of navigating, so the routes need the dialog opener.
function buildRoutes(requiresBilling: boolean, openSupport: () => void) {
  const supportItem = {
    title: "Suporte",
    icon: LifeBuoyIcon,
    onSelect: openSupport
  };
  if (requiresBilling) {
    return [{
      items: [{
        title: "Assinatura",
        url: "/settings/billing",
        icon: CreditCardIcon,
        routeCheck: "loose" as const
      }]
    }, {
      placement: "bottom",
      items: [supportItem]
    }] satisfies SharedNavGroup[];
  }
  return [{
    items: [{
      title: "Dashboard",
      url: "/",
      icon: BarChartIcon,
      routeCheck: "strict" as const
    }, {
      title: "Produtores",
      url: "/producers",
      icon: ListIcon,
      routeCheck: "loose" as const
    }, {
      title: "Projetos",
      url: "/projects",
      icon: FolderOpenDot,
      routeCheck: "loose" as const
    }]
  }, {
    placement: "bottom",
    items: [
    // Settings sections are tabs inside the settings pages.
    {
      title: "Configurações",
      url: "/settings",
      icon: SettingsIcon,
      routeCheck: "loose" as const
    }, supportItem]
  }] satisfies SharedNavGroup[];
}
function GoToAdminLink() {
  const $ = _c(12);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.toString();
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const search = t0;
  const returnTo = search ? `${pathname}?${search}` : pathname;
  let t1;
  if ($[2] !== returnTo) {
    t1 = returnTo ? {
      returnTo
    } : undefined;
    $[2] = returnTo;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = {
      pathname: "/admin",
      query: t1
    };
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const adminHref = t2;
  let t3;
  if ($[6] !== adminHref) {
    t3 = <Link href={adminHref} />;
    $[6] = adminHref;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ShieldCheckIcon className="size-4" />;
    t5 = <span className="group-data-[collapsible=icon]:sr-only">Ir para Admin</span>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3) {
    t6 = <SidebarMenu><SidebarMenuItem><SidebarMenuButton render={t3} className="text-muted-foreground hover:bg-muted/50 hover:text-foreground rounded-md px-2.5 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0" tooltip="Ir para Admin">{t4}{t5}</SidebarMenuButton></SidebarMenuItem></SidebarMenu>;
    $[10] = t3;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
export function AppSidebar(t0) {
  const $ = _c(11);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  const isAdmin = useIsAdmin();
  const openSupport = useOpenSupportDialog();
  const organizationStatus = useQuery(api.check_membership.hasOrganization);
  const requiresBilling = organizationStatus?.hasOrganization === true && organizationStatus.hasActiveSubscription === false;
  let t1;
  if ($[2] !== openSupport || $[3] !== requiresBilling) {
    t1 = buildRoutes(requiresBilling, openSupport);
    $[2] = openSupport;
    $[3] = requiresBilling;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const routes = t1;
  let t2;
  if ($[5] !== isAdmin) {
    t2 = isAdmin ? <GoToAdminLink /> : null;
    $[5] = isAdmin;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props || $[8] !== routes || $[9] !== t2) {
    t3 = <SharedSidebar {...props} data-app-sidebar="true" data-app-sidebar-collapsible="icon" footerTopContent={t2} matchMode="organization" routes={routes} />;
    $[7] = props;
    $[8] = routes;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}